import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  signal,
  inject,
  computed,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import { FormFieldConfig, SearchFormComponent, SearchFormData } from '@shared/components/search-form/search-form.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PermissionService } from '@app/core/services/permission.service';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import { AfterSaleHeaderControllerService } from '@app/api/order-service/api/afterSaleHeaderController.service';
import { RefundPageDTO } from '@app/api/order-service/model/refundPageDTO';
import { RefundPageVO } from '@app/api/order-service/model/refundPageVO';
import { PageInfoRefundPageVO } from '@app/api/order-service/model/pageInfoRefundPageVO';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TagModule } from 'primeng/tag';
import { BlobConverterUtil } from '@app/shared/utils/blob-converter.util';
import { O2OOrderService } from '@app/feature/center-business/o2o/services/o2o-order.service';
import { withPaginatedSearchFields } from '@shared/utils/paginated-field.helpers';
import {
  INTERNAL_RETURN_TABLE_COLUMNS,
  INTERNAL_RETURN_DEFAULT_COLUMNS_ORDER,
  INTERNAL_RETURN_FLEXIBLE_COLUMNS_ORDER,
  INTERNAL_RETURN_ACTION_COLUMN,
  INTERNAL_RETURN_SEARCH_CONFIG,
  INTERNAL_RETURN_DEFAULT_CONFIG,
  RETURN_TYPE_MAP,
  STATUS_MAP,
} from './internal-return.config';

export interface InternalReturn {
  internalReturnNo: string;
  internalOrderNo: string;
  thirdReturnNo: string;
  returnOrderType: string;
  thirdOrderNo: string;
  returnType: string;
  status: string;
  returnOrderSource: string;
  returnRefundReason: string;
  buyerNotes: string;
  csNotes: string;
  returnQty: number;
  buyerRefundTotal: number;
  settlementMethod: string;
  receivingWarehouse: string;
  returnLogisticsInfo: string;
  buyerNickname: string;
  returnerName: string;
  returnerPhone: string;
  returnerAddress: string;
  salesStore: string;
  salesChannel: string;
  thirdReturnTime: string;
  creationDate: string;
  returnCompletedTime: string;
}

@Component({
  selector: 'app-internal-return',
  standalone: true,
  imports: [
    CommonModule,
    AdvancedDataTableComponent,
    SearchFormComponent,
    TranslateModule,
    AppButtonComponent,
    ToastModule,
    TagModule,
  ],
  providers: [MessageService],
  templateUrl: './internal-return.component.html',
  styleUrl: './internal-return.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InternalReturnComponent implements OnInit, OnDestroy {
  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  private translateService = inject(TranslateService);
  private router = inject(Router);
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );
  private afterSaleHeaderService = inject(AfterSaleHeaderControllerService);
  private messageService = inject(MessageService);
  private o2oOrderService = inject(O2OOrderService);
  private destroy$ = new Subject<void>();

  // Search params
  searchParams = signal<SearchFormData>({});

  // Permission flags
  // Using specific submenu 'after-sales_orders' for after-sales permissions
  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('after-sales.after-sales_orders.view');
  });

  // Table state
  items = signal<InternalReturn[]>([]);
  loading = signal(false);
  totalRecords = signal(0);

  // Pagination
  currentPage = signal(1);
  pageSize = signal(INTERNAL_RETURN_DEFAULT_CONFIG.PAGE_SIZE);
  first = signal(0);

  // Sorting
  sortField = signal<string>('creationDate');
  sortOrder = signal<1 | -1>(INTERNAL_RETURN_DEFAULT_CONFIG.DEFAULT_SORT_ASCENDING ? 1 : -1);

  searchFormat = withPaginatedSearchFields(INTERNAL_RETURN_SEARCH_CONFIG, {
    salesStore: {
      loadPage: (params) =>
        this.o2oOrderService.getSalesStoresPage({
          ...params,
          status: true,
        }),
    },
  });

  // Advanced table column configuration
  defaultColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      INTERNAL_RETURN_TABLE_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];
    for (const key of INTERNAL_RETURN_DEFAULT_COLUMNS_ORDER) {
      const col = fromConfig(key);
      if (col) {
        cols.push({
          ...col,
          label: this.translateService.instant(col.label),
        });
      }
    }

    // Conditionally add actions column at the end if permitted
    if (this.canRead()) {
      const actionsCol = fromConfig(INTERNAL_RETURN_ACTION_COLUMN.ACTIONS);
      if (actionsCol) {
        cols.push({
          ...actionsCol,
          label: this.translateService.instant(actionsCol.label),
        });
      }
    }

    return cols;
  });

  flexibleColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      INTERNAL_RETURN_TABLE_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];

    for (const prop of INTERNAL_RETURN_FLEXIBLE_COLUMNS_ORDER) {
      const col = fromConfig(prop);
      if (col) {
        cols.push({
          ...col,
          label: this.translateService.instant(col.label),
        });
      }
    }

    return cols;
  });

  ngOnInit(): void {
    if (!this.canRead()) {
      return;
    }
    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearch(data: SearchFormData): void {
    this.searchParams.set(data);
    this.currentPage.set(1);
    this.first.set(0);
    this.loadData();
  }

  onReset(): void {
    this.searchParams.set({});
    this.currentPage.set(1);
    this.first.set(0);
    this.loadData();
  }

  onPageChange(event: any): void {
    this.currentPage.set((event.page || 0) + 1);
    this.first.set(event.first || 0);
    this.loadData();
  }

  onSortChange(event: { field: string; order: 1 | -1 }): void {
    // Keep sortField as column prop for template comparison
    this.sortField.set(event.field);
    this.sortOrder.set(event.order);
    this.currentPage.set(1);
    this.first.set(0);
    this.loadData();
  }

  onColumnConfigClick(event: Event): void {
    const target = event.target as HTMLElement;
    const pButtonElement = target.closest('p-button');
    const buttonElement = pButtonElement
      ? (pButtonElement.querySelector('button') as HTMLElement)
      : (target.closest('button') as HTMLElement) || target;

    if (buttonElement && this.advancedTable) {
      this.advancedTable.openColumnSelector(buttonElement);
    }
  }

  onViewDetails(record: InternalReturn): void {
    if (!this.canRead()) {
      return;
    }
    this.router.navigate(['/after/internal/return/view', record.internalReturnNo]);
  }

  private loadData(): void {
    if (!this.canRead()) {
      return;
    }
    this.loading.set(true);

    const params = this.searchParams();
    
    // Convert salesStore to storeIdList (array of numbers)
    let storeIdList: number[] | undefined = undefined;
    if (params['salesStore']) {
      if (Array.isArray(params['salesStore'])) {
        storeIdList = params['salesStore'].map((id: any) => Number(id)).filter(id => !isNaN(id));
      } else {
        const storeId = Number(params['salesStore']);
        if (!isNaN(storeId)) {
          storeIdList = [storeId];
        }
      }
    }

    const payload: RefundPageDTO = {
      page: this.currentPage() - 1, // API uses 0-based pagination
      size: this.pageSize(),
      sortBy: this.sortField(),
      ascending: this.sortOrder() === 1,
      orderNo: params['orderNumber'] || undefined,
      actionType: params['actionType'] || undefined,
      buyerNick: params['buyerNickname'] || undefined,
      channelTypeCode: params['channelTypeCode'] || undefined,
      storeIdList: storeIdList && storeIdList.length > 0 ? storeIdList : undefined,
      receiver: params['receiver'] || undefined,
      status: params['status'] || undefined,
      startTime: params['beginCreationDate'] || undefined,
      endTime: params['endCreationDate'] || undefined,
    };

    this.afterSaleHeaderService
      .refundPage(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: PageInfoRefundPageVO) => {
          this.processResponse(response);
        },
        error: (error) => {
          this.handleError(error);
        },
      });
  }

  private processResponse(response: PageInfoRefundPageVO | any): void {
    // Handle blob response
    if (response instanceof Blob) {
      BlobConverterUtil.convertToJson(response).then((data) => {
        this.processRefundData(data);
      }).catch((error) => {
        console.error('Error parsing blob response:', error);
        this.handleError(error);
      });
    } else {
      this.processRefundData(response);
    }
  }

  private processRefundData(response: PageInfoRefundPageVO): void {
    const pageContent = response.pageContent || [];
    const mappedItems: InternalReturn[] = pageContent.map((item: RefundPageVO) =>
      this.mapRefundPageVOToInternalReturn(item)
    );

    this.items.set(mappedItems);
    this.totalRecords.set(response.totalElements || 0);
    this.loading.set(false);
  }

  private mapRefundPageVOToInternalReturn(vo: RefundPageVO): InternalReturn {
    // Map status from number to constant
    const statusNumber = String(vo.status || '');
    const mappedStatus = STATUS_MAP[statusNumber] || statusNumber;
    
    return {
      internalReturnNo: vo.innerRefundNo || '',
      internalOrderNo: vo.innerOrderNo || '',
      thirdReturnNo: vo.outerRefundNo || '',
      returnOrderType: vo.orderRefundType || '',
      thirdOrderNo: vo.outerOrderNo || '',
      returnType: RETURN_TYPE_MAP[vo.type || ''] || '',
      status: mappedStatus,
      returnOrderSource: vo.actionType || '',
      returnRefundReason: vo.reason || '',
      buyerNotes: vo.buyerRemarks || '',
      csNotes: vo.serviceRemarks || vo.remark || '',
      returnQty: vo.goodsQuantity || 0,
      buyerRefundTotal: vo.refundAmount || 0,
      settlementMethod: vo.payType || '',
      receivingWarehouse: vo.receivingWarehouseName || '',
      returnLogisticsInfo: vo.logisticsInfo || '',
      buyerNickname: vo.buyerNick || '',
      returnerName: vo.receiverName || '',
      returnerPhone: vo.mobile || '',
      returnerAddress: vo.address || '',
      salesStore: vo.storeName || '',
      salesChannel: vo.channelTypeMeaning || vo.channel || '',
      thirdReturnTime: vo.outReturnTime || '',
      creationDate: vo.creationDate || '',
      returnCompletedTime: vo.completeDate || '',
    };
  }

  private handleError(error: any): void {
    console.error('Error loading return orders:', error);
    this.loading.set(false);
    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant('common.messages.error'),
      detail: error?.error?.message || this.translateService.instant('after.internal.return.messages.failed_to_load'),
    });
  }

  getStatusLabel(status?: string): string {
    if (!status) return '-';
    
    // Map status constant to translation key
    const statusKeyMap: Record<string, string> = {
      'WAIT_AUDIT': 'after.internal.return.status.wait_audit',
      'WAIT_REFUND': 'after.internal.return.status.wait_refund',
      'COMPLETE': 'after.internal.return.status.completed',
      'REFUSE': 'after.internal.return.status.rejected',
      'REFUND_FAILED': 'after.internal.return.status.refund_failed',
    };
    
    const translationKey = statusKeyMap[status] || status;
    return this.translateService.instant(translationKey);
  }

  getStatusSeverity(status?: string): string {
    if (!status) return 'info';
    
    // Map status constant to PrimeNG tag severity
    const statusSeverityMap: Record<string, string> = {
      'WAIT_AUDIT': 'warning',
      'WAIT_REFUND': 'info',
      'COMPLETE': 'success',
      'REFUSE': 'danger',
      'REFUND_FAILED': 'secondary',
    };
    
    return statusSeverityMap[status] || 'info';
  }

  formatDateTime(dateValue: any): string {
    if (!dateValue) return '-';
    
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) {
        return '-';
      }
      
      // Format as "14:36 20/01/2026"
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      
      return `${hours}:${minutes} ${day}/${month}/${year}`;
    } catch (error) {
      return '-';
    }
  }
} 